import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, Check, ChevronDown, Loader2, Star, X } from "lucide-react";
import { Toast } from "components/ui/Toast";
import { useAuth } from "context/AuthContext";
import { useOnboardingChecklistContext } from "context/OnboardingChecklistContext";
import {
  ChecklistStep,
  ChecklistStepStatus,
} from "hooks/useOnboardingChecklist";

type Scope = "account" | "condo";

type Props = {
  scope: Scope;
  /** Selected building id; required for condo scope so celebrations ignore stale checklists. */
  condoId?: number | null;
  /** When false, the widget stays hidden (e.g. condo scope with no selection). */
  enabled?: boolean;
  onOpenEloriaChat: (contactId: number) => void;
  onEntitiesMaybeChanged?: (
    entity: "condos" | "residents" | "workers" | "providers" | "board_members"
  ) => void;
};

type CelebrationKind = "quest" | "skip" | null;

const ENTITY_LIST_STEP_IDS: Record<
  string,
  "condos" | "residents" | "workers" | "providers"
> = {
  create_first_building: "condos",
  create_residents: "residents",
  create_workers: "workers",
  create_providers: "providers",
};

function statusRank(status: ChecklistStep["status"]): number {
  if (status === "pending") return 0;
  if (status === "not_applicable") return 1;
  return 2;
}

function sortSteps(steps: ChecklistStep[]): ChecklistStep[] {
  return [...steps].sort((a, b) => {
    const byStatus = statusRank(a.status) - statusRank(b.status);
    if (byStatus !== 0) return byStatus;
    if (a.isSuggestedNext !== b.isSuggestedNext) {
      return a.isSuggestedNext ? -1 : 1;
    }
    return a.priority - b.priority;
  });
}

function scopedView(steps: ChecklistStep[], scope: Scope) {
  const scoped = steps.filter((step) => step.scope === scope);
  // N/A counts as progress: the manager resolved the step (skipped on purpose).
  const done = scoped.filter(
    (s) => s.status === "completed" || s.status === "not_applicable"
  );
  const pending = scoped
    .filter((s) => s.status === "pending")
    .sort((a, b) => a.priority - b.priority);
  const suggestedNext = pending[0] ?? null;
  const suggestedNextId = suggestedNext?.id ?? null;
  const ordered = sortSteps(
    scoped.map((step) => ({
      ...step,
      isSuggestedNext: step.id === suggestedNextId,
    }))
  );
  const total = scoped.length;
  const completedCount = done.length;
  return {
    steps: ordered,
    progress: {
      completed: completedCount,
      total,
      percent: total ? Math.round((100 * completedCount) / total) : 100,
    },
    suggestedNext,
    suggestedNextId,
  };
}

const QUEST_CONFETTI = [
  { x: -52, y: -48, delay: "0ms", size: 7, rot: -40, shape: "square", color: "#fbbf24" },
  { x: 48, y: -56, delay: "30ms", size: 6, rot: 25, shape: "circle", color: "#34d399" },
  { x: -64, y: -18, delay: "50ms", size: 5, rot: 15, shape: "rect", color: "#f59e0b" },
  { x: 58, y: -22, delay: "20ms", size: 8, rot: -20, shape: "star", color: "#fcd34d" },
  { x: -38, y: 36, delay: "70ms", size: 6, rot: 35, shape: "square", color: "#10b981" },
  { x: 42, y: 40, delay: "40ms", size: 5, rot: -15, shape: "circle", color: "#fbbf24" },
  { x: 8, y: -62, delay: "10ms", size: 7, rot: 10, shape: "rect", color: "#a7f3d0" },
  { x: -18, y: 52, delay: "80ms", size: 4, rot: -30, shape: "circle", color: "#fde68a" },
  { x: 72, y: 8, delay: "60ms", size: 6, rot: 45, shape: "square", color: "#34d399" },
  { x: -72, y: 12, delay: "25ms", size: 5, rot: -50, shape: "rect", color: "#f59e0b" },
  { x: 28, y: -44, delay: "90ms", size: 9, rot: 5, shape: "star", color: "#fbbf24" },
  { x: -46, y: -38, delay: "55ms", size: 4, rot: 20, shape: "circle", color: "#6ee7b7" },
  { x: 54, y: 28, delay: "100ms", size: 5, rot: -25, shape: "square", color: "#fcd34d" },
  { x: -8, y: -54, delay: "35ms", size: 6, rot: 40, shape: "rect", color: "#34d399" },
  { x: 16, y: 48, delay: "75ms", size: 7, rot: -10, shape: "star", color: "#f59e0b" },
  { x: -56, y: 30, delay: "45ms", size: 4, rot: 55, shape: "circle", color: "#fbbf24" },
] as const;

export function OnboardingChecklistWidget({
  scope,
  condoId = null,
  enabled = true,
  onOpenEloriaChat,
  onEntitiesMaybeChanged,
}: Props) {
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";
  const [open, setOpen] = useState(false);
  const [errorToast, setErrorToast] = useState<string | null>(null);
  const [celebration, setCelebration] = useState<CelebrationKind>(null);
  const [celebratingId, setCelebratingId] = useState<string | null>(null);
  const celebrationTimerRef = useRef<number | null>(null);
  const previousStatusesRef = useRef<Record<string, ChecklistStepStatus>>({});
  const statusBaselineKeyRef = useRef<string | null>(null);
  const chipRef = useRef<HTMLDivElement>(null);
  const [burstOrigin, setBurstOrigin] = useState<{ x: number; y: number } | null>(
    null
  );

  const { checklist, loading, guiding, guideStep } =
    useOnboardingChecklistContext();

  const view = useMemo(
    () => scopedView(checklist?.steps ?? [], scope),
    [checklist?.steps, scope]
  );

  const statusBaselineKey =
    scope === "condo" ? `condo:${checklist?.condoId ?? "none"}` : "account";

  const triggerCelebration = (kind: CelebrationKind) => {
    if (celebrationTimerRef.current) {
      window.clearTimeout(celebrationTimerRef.current);
    }
    setCelebration(kind);
    const duration = kind === "quest" ? 1800 : 900;
    celebrationTimerRef.current = window.setTimeout(() => {
      setCelebration(null);
      setCelebratingId(null);
    }, duration);
  };

  useEffect(() => {
    if (!enabled) {
      previousStatusesRef.current = {};
      statusBaselineKeyRef.current = null;
      return;
    }

    // Condo checklist in context still belongs to the previous building.
    if (
      scope === "condo" &&
      (condoId == null || checklist?.condoId !== condoId)
    ) {
      previousStatusesRef.current = {};
      statusBaselineKeyRef.current = null;
      return;
    }

    if (statusBaselineKeyRef.current !== statusBaselineKey) {
      previousStatusesRef.current = {};
      statusBaselineKeyRef.current = statusBaselineKey;
    }

    const nextMap: Record<string, ChecklistStepStatus> = {};
    const changed: ChecklistStep[] = [];
    for (const step of view.steps) {
      nextMap[step.id] = step.status;
      const prev = previousStatusesRef.current[step.id];
      if (
        prev &&
        prev !== step.status &&
        (step.status === "completed" || step.status === "not_applicable")
      ) {
        changed.push(step);
      }
    }
    // First paint or condo switch: baseline only, no celebration.
    if (Object.keys(previousStatusesRef.current).length === 0) {
      previousStatusesRef.current = nextMap;
      return;
    }
    previousStatusesRef.current = nextMap;

    for (const step of changed) {
      if (step.status === "completed") {
        const entity = ENTITY_LIST_STEP_IDS[step.id];
        if (entity) {
          onEntitiesMaybeChanged?.(entity);
        }
      }
    }
    const completed = changed.find((step) => step.status === "completed");
    if (completed) {
      setCelebratingId(completed.id);
      triggerCelebration("quest");
      return;
    }
    const skipped = changed.find((step) => step.status === "not_applicable");
    if (skipped) {
      setCelebratingId(skipped.id);
      triggerCelebration("skip");
    }
  }, [
    view.steps,
    enabled,
    onEntitiesMaybeChanged,
    scope,
    condoId,
    checklist?.condoId,
    statusBaselineKey,
  ]);

  useEffect(() => {
    return () => {
      if (celebrationTimerRef.current) {
        window.clearTimeout(celebrationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useEffect(() => {
    if (view.progress.total > 0 && view.progress.completed >= view.progress.total) {
      setOpen(false);
    }
  }, [view.progress.completed, view.progress.total]);

  useEffect(() => {
    if (celebration !== "quest") {
      setBurstOrigin(null);
      return;
    }
    const node = chipRef.current;
    if (!node) {
      return;
    }
    const rect = node.getBoundingClientRect();
    setBurstOrigin({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    });
  }, [celebration]);

  if (!enabled) {
    return null;
  }

  if (loading && !checklist) {
    return null;
  }

  if (!checklist || view.progress.total === 0) {
    return null;
  }

  // Avoid flashing the previous building's condo checklist while the new one loads.
  if (scope === "condo" && checklist.condoId !== condoId) {
    return null;
  }

  const isFullyComplete = view.progress.completed >= view.progress.total;
  if (isFullyComplete && celebration == null) {
    return null;
  }

  const percent = view.progress.percent;
  const isQuest = celebration === "quest";
  const isSkip = celebration === "skip";
  const isCelebrating = celebration != null;

  const suggested = view.suggestedNext;
  const objectiveLabel = suggested
    ? locale === "es"
      ? suggested.titleEs
      : suggested.titleEn
    : locale === "es"
      ? `${view.progress.completed}/${view.progress.total} listos`
      : `${view.progress.completed}/${view.progress.total} done`;

  const dialogLabel = locale === "es" ? "Próximo paso" : "Next step";

  const handleStepClick = async (step: ChecklistStep) => {
    if (step.status === "completed" || step.status === "not_applicable" || guiding) {
      return;
    }

    setOpen(false);

    if (checklist.managerContactId) {
      onOpenEloriaChat(checklist.managerContactId);
    }

    try {
      const result = await guideStep(step.id);
      if (result?.managerContactId) {
        onOpenEloriaChat(result.managerContactId);
      }
    } catch {
      setErrorToast(
        locale === "es"
          ? "No se pudo abrir la guía"
          : "Could not open guidance"
      );
    }
  };

  const ProgressBar = () => (
    <div className="relative h-1.5 overflow-hidden rounded-full bg-emerald-100">
      <div
        className={`h-full rounded-full transition-all duration-700 ease-out ${
          isQuest
            ? "bg-gradient-to-r from-amber-400 via-emerald-400 to-amber-300"
            : isSkip
              ? "bg-emerald-400"
              : "bg-emerald-500"
        }`}
        style={{ width: `${percent}%` }}
      />
      {isCelebrating && (
        <div
          className="pointer-events-none absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-white/80 to-transparent"
          style={{
            animation: "onboarding-progress-shine 0.9s ease-out forwards",
          }}
        />
      )}
    </div>
  );

  const popup =
    open &&
    createPortal(
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
        role="presentation"
        onClick={() => setOpen(false)}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-label={dialogLabel}
          className="flex max-h-[min(85vh,40rem)] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/10"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
            <div className="flex min-w-0 items-start gap-3">
              <QuestMarker size="md" />
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900">
                  {dialogLabel}
                </h2>
                <p className="mt-0.5 truncate text-sm font-medium text-emerald-800">
                  {objectiveLabel}
                </p>
                <p
                  className={`mt-0.5 text-sm transition-all duration-500 ${
                    isQuest
                      ? "scale-105 font-semibold text-amber-600"
                      : "text-gray-500"
                  }`}
                >
                  {locale === "es"
                    ? `${view.progress.completed} de ${view.progress.total} completados`
                    : `${view.progress.completed} of ${view.progress.total} completed`}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  {locale === "es"
                    ? "Tocá cualquier paso y Eloria te explica cómo hacerlo."
                    : "Tap any step and Eloria explains how to do it."}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label={locale === "es" ? "Cerrar" : "Close"}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="mx-5 mt-3 shrink-0">
            <ProgressBar />
          </div>

          <ul className="flex-1 space-y-0.5 overflow-y-auto overscroll-y-contain px-3 py-3">
            {view.steps.map((step) => {
              const label = locale === "es" ? step.titleEs : step.titleEn;
              const isSuggested = step.isSuggestedNext;
              const isNa = step.status === "not_applicable";
              const isDone = step.status === "completed";
              const celebrating = celebratingId === step.id;
              const clickable = !isDone && !isNa && !guiding;
              // Exactly one row carries the call to action, so the next move is
              // unambiguous instead of ten rows competing for attention.
              const showCta = isSuggested && step.status === "pending";

              return (
                <li key={step.id}>
                  <button
                    type="button"
                    disabled={!clickable}
                    onClick={() => handleStepClick(step)}
                    className={`flex w-full gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition-all duration-300 ${
                      showCta ? "items-center" : "items-start"
                    } ${
                      isSuggested && !isDone
                        ? "bg-emerald-100/70 text-emerald-950"
                        : "text-gray-700"
                    } ${isNa ? "opacity-60" : ""} ${
                      celebrating && isDone
                        ? "bg-amber-100 ring-2 ring-amber-300 scale-[1.02]"
                        : celebrating
                          ? "bg-emerald-100/80"
                          : ""
                    } ${
                      clickable
                        ? showCta
                          ? "cursor-pointer hover:bg-emerald-100"
                          : "cursor-pointer hover:bg-gray-50"
                        : "cursor-default"
                    }`}
                  >
                    <StatusIcon
                      status={step.status}
                      celebrating={celebrating}
                    />
                    <span
                      className={`min-w-0 flex-1 leading-snug ${
                        isSuggested && !isDone
                          ? "font-bold"
                          : isNa
                            ? "line-through decoration-dashed decoration-gray-300 text-gray-500"
                            : isDone
                              ? "text-gray-500"
                              : "font-normal"
                      }`}
                    >
                      {label}
                    </span>
                    {showCta && (
                      <span className="flex shrink-0 items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white shadow-sm">
                        {guiding ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <>
                            {locale === "es" ? "Cómo hacerlo" : "How to do it"}
                            <ArrowRight className="h-3.5 w-3.5" />
                          </>
                        )}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>,
      document.body
    );

  return (
    <>
      <div
        ref={chipRef}
        className={`relative z-10 mb-3 overflow-visible rounded-lg border bg-gradient-to-br shadow-sm ${
          isQuest
            ? "border-amber-400 from-amber-50 via-emerald-50 to-amber-50"
            : "border-amber-200/80 from-amber-50/40 via-emerald-50 to-white"
        }`}
        style={
          isQuest
            ? { animation: "onboarding-chip-glow 1.2s ease-out" }
            : isSkip
              ? { animation: "onboarding-chip-glow 0.7s ease-out" }
              : undefined
        }
      >
        <div className="flex w-full items-start gap-2.5 px-3 py-2.5">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="flex min-w-0 flex-1 items-start gap-2 text-left transition-colors hover:opacity-90"
            aria-haspopup="dialog"
            aria-expanded={open}
            aria-label={`${dialogLabel}: ${objectiveLabel}`}
          >
            <QuestMarker size="sm" className="mt-0.5" />
            <div className="relative min-w-0 flex-1">
              <span className="line-clamp-2 text-sm font-semibold leading-snug text-emerald-950">
                {objectiveLabel}
              </span>
              <div className="mt-1.5">
                <ProgressBar />
              </div>
            </div>
            <ChevronDown
              className="mt-1 h-4 w-4 shrink-0 text-emerald-700/50"
              strokeWidth={2.25}
              aria-hidden
            />
          </button>

          {suggested ? (
            <button
              type="button"
              disabled={guiding}
              onClick={() => handleStepClick(suggested)}
              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-70"
              aria-label={
                locale === "es" ? "Cómo hacerlo" : "How to do it"
              }
              title={locale === "es" ? "Cómo hacerlo" : "How to do it"}
            >
              {guiding ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              )}
            </button>
          ) : (
            <span
              className={`mt-0.5 shrink-0 text-xs font-bold tabular-nums ${
                isQuest ? "text-amber-600" : "text-emerald-800"
              }`}
            >
              {view.progress.completed}/{view.progress.total}
            </span>
          )}
        </div>
      </div>

      {isQuest &&
        burstOrigin &&
        createPortal(
          <div
            aria-hidden
            className="pointer-events-none fixed inset-0 z-[90] overflow-visible"
          >
            <div
              className="absolute flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-emerald-500 text-white shadow-lg shadow-amber-300/50"
              style={{
                left: burstOrigin.x,
                top: burstOrigin.y,
                animation: "onboarding-quest-check 1.6s ease-in-out forwards",
              }}
            >
              <Check className="h-6 w-6" strokeWidth={3} />
            </div>
            {QUEST_CONFETTI.map((p, i) => (
              <span
                key={i}
                className="absolute"
                style={{
                  left: burstOrigin.x,
                  top: burstOrigin.y,
                  width: p.shape === "rect" ? p.size * 0.55 : p.size,
                  height: p.shape === "rect" ? p.size * 1.4 : p.size,
                  borderRadius:
                    p.shape === "circle"
                      ? "9999px"
                      : p.shape === "star"
                        ? "2px"
                        : "1px",
                  backgroundColor: p.color,
                  boxShadow:
                    p.shape === "star" ? `0 0 6px ${p.color}` : undefined,
                  clipPath:
                    p.shape === "star"
                      ? "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)"
                      : undefined,
                  ["--tx" as string]: `${p.x}px`,
                  ["--ty" as string]: `${p.y}px`,
                  ["--rot" as string]: `${p.rot}deg`,
                  animation: "onboarding-confetti 1.05s ease-out forwards",
                  animationDelay: p.delay,
                }}
              />
            ))}
          </div>,
          document.body
        )}

      {popup}

      {errorToast && (
        <Toast
          message={errorToast}
          type="info"
          onClose={() => setErrorToast(null)}
          duration={3500}
        />
      )}
    </>
  );
}

function QuestMarker({
  size = "sm",
  className = "",
}: {
  size?: "sm" | "md";
  className?: string;
}) {
  const box = size === "md" ? "h-8 w-8" : "h-6 w-6";
  const icon = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";
  return (
    <span
      aria-hidden
      className={`flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-300 via-amber-400 to-amber-500 text-white shadow-sm shadow-amber-300/50 ${box} ${className}`}
    >
      <Star className={icon} strokeWidth={0} fill="currentColor" />
    </span>
  );
}

function StatusIcon({
  status,
  celebrating,
}: {
  status: ChecklistStep["status"];
  celebrating: boolean;
}) {
  if (status === "completed") {
    return (
      <Star
        className={`mt-0.5 h-4 w-4 shrink-0 transition-transform duration-300 ${
          celebrating
            ? "scale-150 text-amber-400"
            : "text-amber-400"
        }`}
        strokeWidth={0}
        fill="currentColor"
      />
    );
  }
  if (status === "not_applicable") {
    return (
      <Star
        className={`mt-0.5 h-4 w-4 shrink-0 text-gray-300 transition-transform duration-300 ${
          celebrating ? "scale-125 text-emerald-400" : ""
        }`}
        strokeWidth={1.5}
        fill="none"
      />
    );
  }
  return (
    <Star
      className="mt-0.5 h-4 w-4 shrink-0 text-amber-400"
      strokeWidth={1.75}
      fill="none"
    />
  );
}
